import React, { FC, memo } from 'react';
import Link from 'next/link';
import { FaCheck, FaArrowRight } from 'react-icons/fa6';
import { FiX } from 'react-icons/fi';

import { BzImage } from '@benzinga/image';
import { AuthIterationType } from '../types';
import { wallStreetCopy } from '../utils';
import { useIsUserLoggedIn } from '@benzinga/user-context';
import { usePaywallTracking } from '../../../hooks/usePaywallTracking';
import {
  buildCheckoutUrl,
  getPaywallConfig,
  getPaywallPoints,
  handlePaywallLoginRedirect,
} from '../../../utils/paywallUtils';

export const WallStreetIteration7: FC<AuthIterationType> = memo(
  ({ allowClose, contentType, placement, setShowPaywall }) => {
    const { ad, hasAltCheckout, paywallCopy, utmSource } = getPaywallConfig(
      contentType,
      wallStreetCopy,
      'wall-street-7',
      'stepahead',
    );

    const checkoutUrl = buildCheckoutUrl({
      ad,
      adType: 'paywall',
      campaign: 'volatility',
      hasAltCheckout,
    });

    const points = getPaywallPoints(paywallCopy, 'PREMIUM_INSIGHTS');

    const isLoggedIn = useIsUserLoggedIn();

    usePaywallTracking({
      allowClose,
      checkoutUrl,
      placement,
      utmSource,
    });

    const handleLoginRedirect = () => handlePaywallLoginRedirect(utmSource);

    return (
      <>
        <div className="fixed inset-0 z-[1000002] touch-none pointer-events-none"></div>
        <div className="paywall-content w-full max-h-[100vh] h-auto animate-fade-up animate-delay-300 animate-once no-scrollbar overflow-scroll flex flex-col md:flex-row items-center md:items-start justify-start md:justify-center md:gap-12 fixed z-[1000003] bottom-0 left-0 bg-white border-t">
          {allowClose && setShowPaywall && (
            <button
              className="absolute top-4 right-4 text-black md:text-white z-10"
              onClick={() => {
                setShowPaywall(false);
              }}
            >
              <FiX />
            </button>
          )}
          <Link className="w-full flex flex-col md:flex-row overflow-hidden" href={checkoutUrl} target="_blank">
            <div className="w-full lg:w-fit ml-0 md:ml-auto">
              <div className="min-h-[232px] lg:min-w-[500px] pr-8">
                <div className="flex flex-col md:flex-row items-start md:items-center p-4 pb-0">
                  <div className="w-32 mr-1">
                    <BzImage height="19px" src="/next-assets/images/bz-edge-logo-dark.svg" width="170px" />
                  </div>
                </div>
                <div className="px-4 py-6 h-full">
                  <span className="text-[#225AA9] tracking-[0.2em] font-bold">
                    {paywallCopy?.subhead ?? 'ALWAYS ONE STEP AHEAD 2025'}
                  </span>
                  <div
                    className="text-4xl py-2 font-extrabold text-[#192940]"
                    dangerouslySetInnerHTML={{
                      __html:
                        paywallCopy?.headline ?? 'Get Up to The <br/> Minute Intelligence <br/> on Every Market Move',
                    }}
                  ></div>
                  <span className="text-[#225AA9] text-sm">{paywallCopy?.text ?? 'Save 43% when you Join Today'}</span>
                </div>
              </div>
              {!isLoggedIn && (
                <div
                  className="w-full bg-[#E9EDF4] px-4 py-2 text-[#5B7292] text-xs z-1 border-none md:border-l border-slategray flex flex-row items-center gap-4 cursor-pointer"
                  onClick={handleLoginRedirect}
                >
                  Already a member?
                  <div className="text-[#225AA9] flex flex-row items-center justify-start gap-1 uppercase font-bold">
                    Login <FaArrowRight className="text-[#225AA9] text-xs" />
                  </div>
                </div>
              )}
            </div>
            <div className="bg-[#0B2040] p-8 relative w-full lg:w-1/2 flex flex-row justify-center md:justify-start">
              <SectionDivider />
              <div className="bg-white p-4 rounded-sm relative w-fit">
                <div className="w-full absolute -top-2 left-0">
                  <div className="bg-[#225AA9] px-2 text-sm font-extrabold text-white mx-auto w-fit uppercase tracking-[0.3em]">
                    members receive
                  </div>
                </div>
                <div className="flex flex-col items-center justify-center gap-4 pt-2 px-8 h-full">
                  <ul className="list-none pb-2 text-base w-fit whitespace-nowrap">
                    {points.map((item, index) => {
                      return (
                        <li className="flex items-center gap-2 text-[#192940] whitespace-nowrap mb-1" key={index}>
                          <FaCheck className="text-bzblue-700" />
                          {item}
                        </li>
                      );
                    })}
                  </ul>
                </div>
                <div className="w-full absolute -bottom-4 left-0">
                  <div className="bg-[#225AA9] px-4 py-2 text-white text-sm font-bold mx-auto w-fit uppercase animate-shake animate-delay-[3000ms]">
                    unlock access now
                  </div>
                </div>
              </div>
            </div>
          </Link>
        </div>
      </>
    );
  },
);

WallStreetIteration7.displayName = 'WallStreetIteration7';

const SectionDivider: React.FC = () => {
  return (
    <div className="diagonal absolute right-0 -top-[20px] md:-left-[60px] md:top-0">
      <style>{`
        .diagonal {
          width: 61px;
          height: 500px;
          background-color: #0b2040;

          clip-path: polygon(100% 0, 0 100%, 100% 100%);
        }
        @media (max-width: 768px) {
          .diagonal {
            height: 21px;
            width: 100vw;
          }
        }
      `}</style>
    </div>
  );
};
